import { Button, Card, cn } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import type { KPICardData } from "../../domain/types";

interface KPICardProps {
  kpi: KPICardData;
  /** ປຸ່ມອະທິບາຍຜົນ (ພາບລວມ Phase A) — ບໍ່ກະທົບການຄລິກການເປີດນິຍາມ KPI */
  onExplain?: (kpi: KPICardData) => void;
}

export function KPICard({ kpi, onExplain }: KPICardProps) {
  const navigate = useNavigate({ from: "/app/kpi" });

  const statusConfig = {
    pass: {
      icon: CheckCircle2,
      bgColor: "bg-green-50 dark:bg-green-950/20",
      borderColor: "border-green-200 dark:border-green-800",
      textColor: "text-green-700 dark:text-green-400",
      iconColor: "text-green-600 dark:text-green-500",
      label: "ຜ່ານ",
    },
    warning: {
      icon: AlertCircle,
      bgColor: "bg-yellow-50 dark:bg-yellow-950/20",
      borderColor: "border-yellow-200 dark:border-yellow-800",
      textColor: "text-yellow-700 dark:text-yellow-400",
      iconColor: "text-yellow-600 dark:text-yellow-500",
      label: "ເກືອບຜ່ານ",
    },
    fail: {
      icon: XCircle,
      bgColor: "bg-red-50 dark:bg-red-950/20",
      borderColor: "border-red-200 dark:border-red-800",
      textColor: "text-red-700 dark:text-red-400",
      iconColor: "text-red-600 dark:text-red-500",
      label: "ຕໍ່າກວ່າເປົ້າໝາຍ",
    },
    pending: {
      icon: AlertCircle,
      bgColor: "bg-gray-50 dark:bg-gray-950/20",
      borderColor: "border-gray-200 dark:border-gray-800",
      textColor: "text-gray-700 dark:text-gray-400",
      iconColor: "text-gray-600 dark:text-gray-500",
      label: "ກຳລັງວັດ",
    },
  };

  const config = statusConfig[kpi.status];
  const Icon = config.icon;

  const handleClick = () => {
    navigate({ to: "/app/kpi/$id", params: { id: kpi.id } });
  };

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        config.bgColor,
        config.borderColor,
        "border-2",
      )}
      onClick={handleClick}
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="mb-2 font-semibold text-sm">{kpi.name}</h3>
            <div className="flex items-center gap-4">
              <div>
                <p className={cn("font-medium text-xs", config.textColor)}>
                  ຄ່າຈິງ
                </p>
                <p className={cn("font-bold text-lg", config.textColor)}>
                  {typeof kpi.actualValue === "number"
                    ? kpi.actualValue.toLocaleString()
                    : Number(kpi.actualValue || 0).toLocaleString()}
                </p>
              </div>
              <div className="text-muted-foreground">
                <p className="text-xs">/</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">ເປົ້າໝາຍ</p>
                <p className="font-semibold text-lg text-muted-foreground">
                  {typeof kpi.targetValue === "number"
                    ? kpi.targetValue.toLocaleString()
                    : Number(kpi.targetValue || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Icon className={cn("h-5 w-5", config.iconColor)} />
            <span className={cn("font-medium text-xs", config.textColor)}>
              {config.label}
            </span>
          </div>
        </div>
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-muted-foreground text-xs">ອັດຕາສ່ວນຜ່ານ</span>
            <span className={cn("font-medium text-xs", config.textColor)}>
              {kpi.passRate.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
            <div
              className={cn(
                "h-full transition-all",
                kpi.status === "pass" && "bg-green-600 dark:bg-green-500",
                kpi.status === "warning" && "bg-yellow-500 dark:bg-yellow-400",
                kpi.status === "fail" && "bg-red-600 dark:bg-red-500",
                kpi.status === "pending" && "bg-gray-400 dark:bg-gray-500",
              )}
              style={{ width: `${Math.min(100, kpi.passRate)}%` }}
            />
          </div>
        </div>
        {onExplain && (
          <div className="mt-3 flex justify-end border-border border-t pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onExplain(kpi);
              }}
            >
              ລາຍລະອຽດຜົນ
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
